import { useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useApi } from '../hooks/useApi';
import { Button, Input } from '../components/ui';

/**
 * Master Data AI — AI-assisted maintenance of the commercial master data.
 *
 * Two modes, one governed write path:
 *  • Upload — drop any Excel/CSV; the AI maps its columns onto the chosen
 *    master (Scan Items / Store Master / Ranging); you review the mapping.
 *  • Chat — ask in natural language ("deactivate all discontinued Coles SKUs",
 *    "range Bhuja Original across QLD Woolworths"); the agent queries current
 *    data and proposes a change-set.
 *
 * Both modes produce import-ready rows that funnel through the existing,
 * governed import endpoint (dry-run → apply) with full audit history. The AI
 * only proposes; nothing is written until you click Apply.
 */

const bit = (v) => (['0', 'false', 'n', 'no', '', 'delisted', 'inactive', 'removed'].includes(String(v ?? '').trim().toLowerCase()) ? 0 : 1);

const ENTITIES = {
  'scan-items': {
    label: 'Scan Items',
    importRoute: '/scan-items/import',
    fields: [
      ['ean', 'EAN (key)'], ['majansItemCode', 'Majans Item Code'], ['productDescription', 'Product Description'],
      ['subCategory', 'Sub-Category'], ['segment', 'Segment'], ['brand', 'Brand'], ['manufacturer', 'Manufacturer'],
      ['sizeGrouping', 'Size Grouping'], ['sizeG', 'Size (g)'], ['flavourCategory', 'Flavour Category'],
      ['flavours', 'Flavours'], ['base', 'Base'], ['assortment', 'Assortment'], ['priceTier', 'Price Tier'],
      ['isActive', 'Active'], ['removed', 'Removed'], ['ownBrand', 'Own Brand'],
    ],
    keyFields: ['ean'],
    keyOk: (m) => !!m.ean,
    shape: 'flat',
    buildRows: (rows, map) => flatRows(rows, map, (o) => !!o.ean),
    changeCount: (p) => (p.adds ?? 0) + (p.updates ?? 0),
    previewText: (p) => `${p.total.toLocaleString()} rows · ${p.adds} new · ${p.updates} updates · ${p.errors.length} errors`,
    resultText: (r) => `${r.adds} new · ${r.updates} updated · ${r.errors.length} errors`,
  },
  'store-master': {
    label: 'Store Master',
    importRoute: '/stores/import',
    fields: [
      ['storeRef', 'Store Ref (key)'], ['customerCode', 'Customer Code'], ['storeNumber', 'Store Number'], ['channel', 'Channel'],
      ['banner', 'Banner'], ['storeName', 'Store Name'], ['customerFacingName', 'Customer Facing Name'], ['isActive', 'Active'],
      ['address', 'Address'], ['suburb', 'Suburb'], ['postcode', 'Postcode'], ['state', 'State'], ['phone', 'Phone'],
      ['latitude', 'Latitude'], ['longitude', 'Longitude'], ['region', 'Region'], ['cluster', 'Cluster'],
      ['storeSize', 'Store Size'], ['repName', 'Rep Name'], ['repTerritory', 'Rep Territory'],
    ],
    keyFields: ['storeRef', 'customerCode', 'storeNumber'],
    keyOk: (m) => !!m.storeRef || (!!m.customerCode && !!m.storeNumber),
    shape: 'flat',
    buildRows: (rows, map) => flatRows(rows, map, (o) => !!o.storeRef || (!!o.customerCode && !!o.storeNumber)),
    changeCount: (p) => (p.adds?.length ?? 0) + (p.changes?.length ?? 0) + (p.deactivations?.length ?? 0),
    previewText: (p) => `${p.adds.length} new · ${p.changes.length} changes · ${p.deactivations.length} deactivations · ${p.errors.length} errors`,
    resultText: (r) => `${r.adds.length} new · ${r.changes.length} changed · ${r.deactivations.length} deactivated`,
  },
  ranging: {
    label: 'Ranging',
    importRoute: '/ranging/import',
    fields: [
      ['storeRef', 'Store Ref (key)'], ['customerCode', 'Customer Code'], ['storeNumber', 'Store Number'],
      ['ean', 'EAN (key)'], ['ranged', 'Ranged (key)'],
    ],
    keyFields: ['storeRef', 'customerCode', 'storeNumber', 'ean', 'ranged'],
    keyOk: (m) => (!!m.storeRef || (!!m.customerCode && !!m.storeNumber)) && !!m.ean && !!m.ranged,
    shape: 'ranging',
    buildRows: (rows, map) => rangingRows(rows, map),
    changeCount: (p) => (p.opens ?? 0) + (p.closes ?? 0),
    previewText: (p) => `${p.storesInFile} stores · ${p.opens} opens · ${p.closes} closes · ${p.unknownEans.length} unknown EANs · ${p.errors.length} errors`,
    resultText: (r) => `${r.opens} opened · ${r.closes} closed`,
  },
  'rebate-master': {
    label: 'Rebate Master',
    importRoute: '/commercial-master/rebate-master/import',
    fields: [['customerCode', 'Customer Code (key)'], ['rebatePct', 'Rebate % (fraction, 0.085)']],
    keyFields: ['customerCode'],
    keyOk: (m) => !!m.customerCode,
    shape: 'flat', chat: false,
    buildRows: (rows, map) => flatRows(rows, map, (o) => !!o.customerCode),
    changeCount: (p) => (p.adds ?? 0) + (p.updates ?? 0),
    previewText: (p) => `${p.total.toLocaleString()} rows · ${p.adds} new · ${p.updates} updates · ${p.errors.length} errors`,
    resultText: (r) => `${r.adds} new · ${r.updates} updated · ${r.errors.length} errors`,
  },
  'transport-allocation': {
    label: 'Transport Allocation',
    importRoute: '/commercial-master/transport-allocation/import',
    fields: [
      ['customerCode', 'Customer Code (key)'], ['dc', 'DC (key)'], ['shipToCustomerCode', 'Ship-To Code'],
      ['warehouse', 'Warehouse'], ['dcCode', 'DC Code'], ['state', 'State'],
      ['leadTimeDays', 'Lead Time (days)'], ['allocationPct', 'Allocation'], ['customerName', 'Customer Name'],
    ],
    keyFields: ['customerCode', 'dc'],
    keyOk: (m) => !!m.customerCode && !!m.dc,
    shape: 'flat', chat: false,
    buildRows: (rows, map) => flatRows(rows, map, (o) => !!o.customerCode && !!o.dc),
    changeCount: (p) => (p.adds ?? 0) + (p.updates ?? 0),
    previewText: (p) => `${p.total.toLocaleString()} rows · ${p.adds} new · ${p.updates} updates · ${p.errors.length} errors`,
    resultText: (r) => `${r.adds} new · ${r.updates} updated · ${r.errors.length} errors`,
  },
  'rrp-items': {
    label: 'RRP Items',
    importRoute: '/commercial-master/rrp-items/import',
    fields: [['customerCode', 'Customer Code (key)'], ['itemNumber', 'Item Number (key)'], ['brand', 'Brand']],
    keyFields: ['customerCode', 'itemNumber'],
    keyOk: (m) => !!m.customerCode && !!m.itemNumber,
    shape: 'flat', chat: false,
    buildRows: (rows, map) => flatRows(rows, map, (o) => !!o.customerCode && !!o.itemNumber),
    changeCount: (p) => (p.adds ?? 0) + (p.updates ?? 0),
    previewText: (p) => `${p.total.toLocaleString()} rows · ${p.adds} new · ${p.updates} updates · ${p.errors.length} errors`,
    resultText: (r) => `${r.adds} new · ${r.updates} updated · ${r.errors.length} errors`,
  },
  'promo-rrp': {
    label: 'Promo RRP',
    importRoute: '/commercial-master/promo-rrp/import',
    fields: [
      ['customerCode', 'Customer Code (key)'], ['brand', 'Brand (key)'], ['promoType', 'Promo Type (key)'],
      ['fromDate', 'From Date (key)'], ['toDate', 'To Date'], ['rrPrice', 'RRP'],
      ['redemption', 'Redemption (0–1)'], ['rrPrice2', 'RRP 2'],
    ],
    keyFields: ['customerCode', 'brand', 'promoType', 'fromDate'],
    keyOk: (m) => !!m.customerCode && !!m.brand && !!m.promoType && !!m.fromDate,
    shape: 'flat', chat: false,
    buildRows: (rows, map) => flatRows(rows, map, (o) => !!o.customerCode && !!o.brand && !!o.promoType && !!o.fromDate),
    changeCount: (p) => (p.adds ?? 0) + (p.updates ?? 0),
    previewText: (p) => `${p.total.toLocaleString()} rows · ${p.adds} new · ${p.updates} updates · ${p.errors.length} errors`,
    resultText: (r) => `${r.adds} new · ${r.updates} updated · ${r.errors.length} errors`,
  },
};

// Flat: one import row per file row, keyed by Majans field names.
function flatRows(rows, map, hasKey) {
  return rows
    .map((r) => {
      const o = {};
      for (const [field, header] of Object.entries(map)) {
        if (!header) continue;
        const v = r[header];
        if (v !== '' && v !== null && v !== undefined) o[field] = v;
      }
      return o;
    })
    .filter(hasKey);
}

// Ranging: pivot long rows (store, ean, ranged) into {storeRef, flags:{ean:0|1}}.
function rangingRows(rows, map) {
  const group = {};
  for (const r of rows) {
    const ref = map.storeRef ? String(r[map.storeRef] ?? '').trim()
      : (map.customerCode && map.storeNumber ? `${String(r[map.customerCode] ?? '').trim()}${String(r[map.storeNumber] ?? '').trim()}` : '');
    const ean = map.ean ? String(r[map.ean] ?? '').trim() : '';
    if (!ref || !ean || !map.ranged) continue;
    (group[ref] ??= { storeRef: ref, flags: {} }).flags[ean] = bit(r[map.ranged]);
  }
  return Object.values(group);
}

const CONF_COLOR = { high: 'var(--success)', medium: 'var(--warning)', low: 'var(--danger)' };

export default function MasterDataAI() {
  const apiFetch = useApi();
  const qc = useQueryClient();

  const [entityKey, setEntityKey] = useState('scan-items');
  const [mode, setMode] = useState('upload');
  const entity = ENTITIES[entityKey];

  // Shared proposal funnel: { rows, note } from either mode → preview → apply.
  const [proposal, setProposal] = useState(null);   // { rows, note }
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(null);           // 'preview' | 'apply'
  const [error, setError] = useState(null);

  function resetProposal() { setProposal(null); setPreview(null); setResult(null); setError(null); }
  function changeEntity(k) { setEntityKey(k); resetProposal(); if (ENTITIES[k]?.chat === false) setMode('upload'); }

  // Receive a staged change-set from Upload or Chat; auto dry-run.
  async function stage(rows, note) {
    setProposal({ rows, note }); setPreview(null); setResult(null); setError(null);
    await runImport(rows, true, note);
  }

  // `note` is passed explicitly (state may not have flushed yet on the first
  // dry-run); falls back to the settled proposal note for the Apply click.
  async function runImport(rows, dryRun, note) {
    if (!rows?.length) { setError('Nothing to import.'); return; }
    const label = note ?? proposal?.note;
    setBusy(dryRun ? 'preview' : 'apply'); setError(null);
    try {
      const res = await apiFetch(entity.importRoute, {
        method: 'POST',
        body: JSON.stringify({ rows, dryRun, fileName: label ? `AI: ${label}` : 'AI master-data' }),
      });
      if (!res.ok) { const b = await res.json().catch(() => ({})); throw new Error(b?.error || `Import failed (HTTP ${res.status})`); }
      const data = await res.json();
      if (dryRun) { setPreview(data); setResult(null); }
      else {
        setResult(data);
        qc.invalidateQueries({ queryKey: ['scan-items'] });
        qc.invalidateQueries({ queryKey: ['scan-item-stats'] });
        qc.invalidateQueries({ queryKey: ['stores'] });
        qc.invalidateQueries({ queryKey: ['ranging-matrix'] });
      }
    } catch (err) { setError(String(err.message ?? err)); }
    finally { setBusy(null); }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14, maxWidth: 860 }}>
      <div style={{ fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.5 }}>
        AI-assisted maintenance of your master data. Upload a spreadsheet in any layout, or just ask — the AI proposes
        changes and you review the diff before anything is written, through the same governed import with audit history.
      </div>

      {/* Entity picker */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Master</span>
        {Object.entries(ENTITIES).map(([k, e]) => (
          <Button key={k} size="sm" variant={k === entityKey ? 'primary' : 'default'} onClick={() => changeEntity(k)}>{e.label}</Button>
        ))}
        <span style={{ flex: 1 }} />
        <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Mode</span>
        <Button size="sm" variant={mode === 'upload' ? 'ai' : 'default'} onClick={() => { setMode('upload'); resetProposal(); }}>Upload</Button>
        <Button size="sm" variant={mode === 'chat' ? 'ai' : 'default'} disabled={entity.chat === false}
          title={entity.chat === false ? 'Chat editing not available for this master yet — use Upload' : undefined}
          onClick={() => { setMode('chat'); resetProposal(); }}>Chat</Button>
      </div>

      {mode === 'upload'
        ? <UploadPanel key={entityKey} entity={entity} entityKey={entityKey} apiFetch={apiFetch} onStage={stage} />
        : <ChatPanel key={entityKey} entity={entity} entityKey={entityKey} apiFetch={apiFetch} onStage={stage} />}

      {/* Shared preview / apply */}
      {error && <div style={{ color: 'var(--danger)', fontSize: 13 }}>{error}</div>}
      {proposal && (preview || busy === 'preview') && !result && (
        <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>Preview {proposal.note ? <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>— {proposal.note}</span> : null}</div>
          {busy === 'preview' && <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Checking against current data…</div>}
          {preview && <div style={{ fontSize: 13, color: 'var(--text-body)' }}>{entity.previewText(preview)}</div>}
          {preview?.errors?.length > 0 && (
            <ul style={{ margin: 0, paddingLeft: 18, maxHeight: 120, overflow: 'auto', fontSize: 12, color: 'var(--text-muted)' }}>
              {preview.errors.slice(0, 25).map((e, i) => <li key={i}>Row {e.row}: {e.error}</li>)}
            </ul>
          )}
          {preview && (
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button size="sm" variant="ai" disabled={busy === 'apply' || entity.changeCount(preview) === 0} onClick={() => runImport(proposal.rows, false)}>
                {busy === 'apply' ? 'Applying…' : `Apply ${entity.changeCount(preview).toLocaleString()} changes`}
              </Button>
            </div>
          )}
        </div>
      )}
      {result && (
        <div style={{ background: 'var(--success-soft, var(--bg-card))', border: '1px solid var(--success-border, var(--border))', borderRadius: 'var(--radius)', padding: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>Applied</div>
          <div style={{ fontSize: 13, color: 'var(--text-body)' }}>{entity.resultText(result)}. Live in {entity.label} with audit history.</div>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}><Button size="sm" onClick={resetProposal}>Done</Button></div>
        </div>
      )}
    </div>
  );
}

/* ── Upload mode ──────────────────────────────────────────────────────────── */
function UploadPanel({ entity, entityKey, apiFetch, onStage }) {
  const fileRef = useRef(null);
  const [parsed, setParsed] = useState(null);   // { fileName, headers, rows }
  const [ai, setAi] = useState(null);
  const [override, setOverride] = useState({});
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const [dragOver, setDragOver] = useState(false);

  function reset() { setParsed(null); setAi(null); setOverride({}); setError(null); }

  async function handleFile(file) {
    if (!file) return;
    reset(); setBusy('parse');
    try {
      const XLSX = await import('xlsx');
      const wb = XLSX.read(await file.arrayBuffer(), { cellDates: true });   // native dates as Date, not serials
      const rows = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], { defval: '' });
      if (!rows.length) throw new Error('That file has no data rows.');
      const headers = [...new Set(rows.flatMap((r) => Object.keys(r)))];
      const p = { fileName: file.name, headers, rows };
      setParsed(p);
      await mapWithAi(p);
    } catch (err) { setError(String(err.message ?? err)); }
    finally { setBusy(null); }
  }

  async function mapWithAi(p) {
    setBusy('map'); setError(null);
    try {
      const res = await apiFetch('/ai/master-data/map', {
        method: 'POST',
        body: JSON.stringify({ entity: entityKey, headers: p.headers, sampleRows: p.rows.slice(0, 20) }),
      });
      if (res.status === 503) { const b = await res.json().catch(() => ({})); throw new Error(b?.detail || 'AI mapping is not configured (no API key).'); }
      if (!res.ok) { const b = await res.text().catch(() => ''); throw new Error(`AI mapping failed: HTTP ${res.status} ${b.slice(0, 200)}`); }
      const data = await res.json();
      setAi(data); setOverride({ ...data.mapping });
    } catch (err) { setError(String(err.message ?? err)); }
    finally { setBusy(null); }
  }

  const mappedRows = useMemo(() => (parsed && ai ? entity.buildRows(parsed.rows, override) : []), [parsed, ai, override, entity]);
  const keyOk = entity.keyOk(override);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div
        onClick={() => fileRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => { e.preventDefault(); setDragOver(false); handleFile(e.dataTransfer.files?.[0]); }}
        style={{ border: `1.5px dashed ${dragOver ? 'var(--ai)' : 'var(--border)'}`, background: dragOver ? 'var(--ai-soft)' : 'var(--bg-card)', borderRadius: 'var(--radius)', padding: '24px 20px', textAlign: 'center', cursor: 'pointer' }}
      >
        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>
          {parsed ? parsed.fileName : `Drop a ${entity.label} spreadsheet, or click to choose`}
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
          {parsed ? `${parsed.rows.length.toLocaleString()} rows · ${parsed.headers.length} columns` : '.xlsx, .xls or .csv — any column layout'}
        </div>
        <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" style={{ display: 'none' }}
          onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; handleFile(f); }} />
      </div>

      {busy === 'parse' && <Note>Reading file…</Note>}
      {busy === 'map' && <Note>AI is mapping your columns…</Note>}
      {error && <div style={{ color: 'var(--danger)', fontSize: 13 }}>{error}</div>}

      {ai && (
        <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>AI column mapping</div>
            <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 999, color: CONF_COLOR[ai.confidence] || 'var(--text-muted)', border: `1px solid ${CONF_COLOR[ai.confidence] || 'var(--border-soft)'}` }}>{ai.confidence} confidence</span>
            <span style={{ flex: 1 }} />
            <Button size="sm" onClick={() => mapWithAi(parsed)} disabled={busy === 'map'}>Re-map</Button>
          </div>
          {!keyOk && <div style={{ fontSize: 12, color: 'var(--danger)' }}>Map the key column(s) below — required to match records.</div>}
          {ai.warnings?.length > 0 && (
            <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, color: 'var(--text-muted)' }}>{ai.warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 16px' }}>
            {entity.fields.map(([field, label]) => {
              const isKey = entity.keyFields.includes(field);
              return (
                <div key={field} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ width: 140, fontSize: 12, fontWeight: isKey ? 700 : 500, color: isKey ? 'var(--text-primary)' : 'var(--text-body)' }}>{label}</div>
                  <select value={override[field] || ''} onChange={(e) => setOverride((m) => ({ ...m, [field]: e.target.value }))}
                    style={{ flex: 1, fontSize: 12, padding: '5px 8px', background: 'var(--bg-input)', color: 'var(--text-primary)', border: `1px solid ${isKey && !override[field] ? 'var(--danger)' : 'var(--border-soft)'}`, borderRadius: 'var(--radius)' }}>
                    <option value="">— not mapped —</option>
                    {parsed.headers.map((h) => <option key={h} value={h}>{h}</option>)}
                  </select>
                </div>
              );
            })}
          </div>
          {ai.unmappedHeaders?.length > 0 && <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Unused columns: {ai.unmappedHeaders.join(', ')}</div>}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', flex: 1 }}>{mappedRows.length.toLocaleString()} of {parsed.rows.length.toLocaleString()} rows usable.</div>
            <Button size="sm" variant="primary" disabled={!keyOk || !mappedRows.length} onClick={() => onStage(mappedRows, parsed.fileName)}>Preview changes</Button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Chat mode ────────────────────────────────────────────────────────────── */
const SUGGESTIONS = {
  'scan-items': ['How many own-brand items still need review?', 'Set sub-category to "NUTS & JERKY" for all items where brand = ALMONDS'],
  'store-master': ['How many active Woolworths stores are in QLD?', 'Set region to "Far North" for all QLD stores'],
  ranging: ['Range Bhuja Original Mix across all QLD Woolworths stores', 'How many Coles stores are there?'],
};

function ChatPanel({ entity, entityKey, apiFetch, onStage }) {
  const [messages, setMessages] = useState([]);   // {role, content}
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);

  async function send(text) {
    const q = (text ?? input).trim();
    if (!q || sending) return;
    setInput(''); setError(null);
    const next = [...messages, { role: 'user', content: q }];
    setMessages(next); setSending(true);
    try {
      const res = await apiFetch('/ai/master-data/agent', { method: 'POST', body: JSON.stringify({ entity: entityKey, messages: next }) });
      if (res.status === 503) { const b = await res.json().catch(() => ({})); throw new Error(b?.detail || 'AI is not configured (no API key).'); }
      if (!res.ok) { const b = await res.text().catch(() => ''); throw new Error(`Agent failed: HTTP ${res.status} ${b.slice(0, 200)}`); }
      const data = await res.json();
      setMessages((m) => [...m, { role: 'assistant', content: data.reply || '(no reply)' }]);
      if (data.proposal?.rows?.length) onStage(data.proposal.rows, data.proposal.summary);
    } catch (err) {
      setError(String(err.message ?? err));
      setMessages((m) => [...m, { role: 'assistant', content: '⚠️ ' + String(err.message ?? err) }]);
    } finally { setSending(false); }
  }

  return (
    <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, minHeight: 120, maxHeight: 360, overflow: 'auto' }}>
        {messages.length === 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Ask me to find or bulk-edit {entity.label}. For example:</div>
            {(SUGGESTIONS[entityKey] || []).map((s) => (
              <button key={s} onClick={() => send(s)} disabled={sending}
                style={{ textAlign: 'left', fontSize: 12, color: 'var(--ai-text)', background: 'var(--ai-soft)', border: '1px solid var(--ai-border)', borderRadius: 'var(--radius)', padding: '7px 10px', cursor: 'pointer' }}>{s}</button>
            ))}
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} style={{ alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start', maxWidth: '85%', background: m.role === 'user' ? 'var(--info-soft)' : 'var(--bg-input)', border: `1px solid ${m.role === 'user' ? 'var(--info-border)' : 'var(--border-soft)'}`, borderRadius: 'var(--radius)', padding: '8px 11px', fontSize: 13, color: 'var(--text-body)', whiteSpace: 'pre-wrap' }}>{m.content}</div>
        ))}
        {sending && <div style={{ alignSelf: 'flex-start', fontSize: 12, color: 'var(--text-muted)' }}>Thinking…</div>}
      </div>
      {error && <div style={{ fontSize: 12, color: 'var(--danger)' }}>{error}</div>}
      <div style={{ display: 'flex', gap: 8 }}>
        <Input placeholder={`Ask about ${entity.label}…`} value={input} onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }} disabled={sending} />
        <Button size="sm" variant="ai" disabled={sending || !input.trim()} onClick={() => send()}>Send</Button>
      </div>
    </div>
  );
}

function Note({ children }) {
  return <div style={{ fontSize: 13, color: 'var(--text-muted)', padding: '4px 0' }}>{children}</div>;
}
